import config from "../config.js";
import { apiFetch } from "./apiClient.js";

export const GUEST_HANDOFF_TOKEN_KEY = "chat_handoff_token";
export const GUEST_HANDOFF_PENDING_KEY = "pending_handoff_login";
export const GUEST_HANDOFF_INTENT_KEY = "chat_handoff_intent";
export const GUEST_HANDOFF_TRANSFERRED_KEY = "chat_handoff_transferred";
export const GUEST_HANDOFF_STATUS_KEY = "chat_handoff_status";
export const GUEST_LOGIN_DRAFT_KEY = "guest_chat_login_draft";
const LOGIN_DRAFT_MAX_AGE = 30 * 60 * 1000;

export const isUnavailableGuestSession = (status) =>
  ["no_guest_session", "guest_session_expired"].includes(status);

const clearPendingGuestChatHandoff = () => {
  sessionStorage.removeItem(GUEST_HANDOFF_TOKEN_KEY);
  sessionStorage.removeItem(GUEST_HANDOFF_PENDING_KEY);
};

export const prepareGuestChatLogin = ({
  handoffToken,
  intent = "continue",
} = {}) => {
  if (!handoffToken) return false;

  sessionStorage.removeItem(GUEST_LOGIN_DRAFT_KEY);
  sessionStorage.removeItem(GUEST_HANDOFF_STATUS_KEY);
  sessionStorage.setItem(GUEST_HANDOFF_TOKEN_KEY, handoffToken);
  sessionStorage.setItem(GUEST_HANDOFF_PENDING_KEY, "true");
  sessionStorage.setItem(GUEST_HANDOFF_INTENT_KEY, intent);
  return true;
};

export const hasPendingGuestChatHandoff = () =>
  sessionStorage.getItem(GUEST_HANDOFF_PENDING_KEY) === "true" &&
  Boolean(sessionStorage.getItem(GUEST_HANDOFF_TOKEN_KEY));

export const shouldSuppressAppDownloadDuringHandoff = (isChatMode) =>
  Boolean(isChatMode) || hasPendingGuestChatHandoff();

export const completeGuestChatHandoff = async () => {
  if (!hasPendingGuestChatHandoff()) return false;

  const handoffToken = sessionStorage.getItem(GUEST_HANDOFF_TOKEN_KEY);
  const intent = sessionStorage.getItem(GUEST_HANDOFF_INTENT_KEY) || "continue";

  const response = await apiFetch(`${config.serverUri}/private/query/chat/handoff`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ handoff_token: handoffToken }),
  });
  const result = await response.json().catch(() => ({}));
  const status = result.status || (response.ok ? "unknown" : "server_error");

  if (!response.ok || status === "server_error") {
    sessionStorage.setItem(GUEST_HANDOFF_STATUS_KEY, "server_error");
    throw new Error("guest chat handoff failed");
  }

  if (result.transferred === true || status === "transferred") {
    sessionStorage.setItem(GUEST_HANDOFF_TRANSFERRED_KEY, "true");
    sessionStorage.setItem(GUEST_HANDOFF_INTENT_KEY, intent);
    sessionStorage.removeItem(GUEST_HANDOFF_STATUS_KEY);
    clearPendingGuestChatHandoff();
    return true;
  }

  sessionStorage.setItem(GUEST_HANDOFF_STATUS_KEY, status);
  if (status !== "user_session_exists") {
    clearPendingGuestChatHandoff();
  }
  return false;
};

export const consumeTransferredGuestChat = () => {
  if (sessionStorage.getItem(GUEST_HANDOFF_TRANSFERRED_KEY) !== "true") {
    return null;
  }

  const intent = sessionStorage.getItem(GUEST_HANDOFF_INTENT_KEY) || "continue";
  sessionStorage.removeItem(GUEST_HANDOFF_TRANSFERRED_KEY);
  sessionStorage.removeItem(GUEST_HANDOFF_INTENT_KEY);
  return { intent };
};

export const consumeGuestHandoffStatus = () => {
  const status = sessionStorage.getItem(GUEST_HANDOFF_STATUS_KEY);
  sessionStorage.removeItem(GUEST_HANDOFF_STATUS_KEY);
  return status;
};

export const discardPendingGuestChatHandoff = () => {
  clearPendingGuestChatHandoff();
  sessionStorage.removeItem(GUEST_HANDOFF_INTENT_KEY);
  sessionStorage.removeItem(GUEST_HANDOFF_STATUS_KEY);
};

// Called only after the server attests a missing/expired guest session and the
// user selects a login method again. This is an unsent input, never history.
export const prepareUnavailableGuestChatLogin = ({ status, draft = "" } = {}) => {
  if (!isUnavailableGuestSession(status) || typeof draft !== "string" || draft.length > 1000) {
    return false;
  }
  try {
    sessionStorage.setItem(GUEST_LOGIN_DRAFT_KEY, JSON.stringify({ text: draft, status, savedAt: Date.now() }));
    discardPendingGuestChatHandoff();
    sessionStorage.removeItem(GUEST_HANDOFF_TRANSFERRED_KEY);
    sessionStorage.setItem(GUEST_HANDOFF_STATUS_KEY, status);
    return true;
  } catch (_) {
    return false;
  }
};

export const consumeUnavailableGuestChatDraft = () => {
  try {
    const raw = sessionStorage.getItem(GUEST_LOGIN_DRAFT_KEY);
    sessionStorage.removeItem(GUEST_LOGIN_DRAFT_KEY);
    if (!raw) return "";
    const draft = JSON.parse(raw);
    const age = Date.now() - draft.savedAt;
    return isUnavailableGuestSession(draft.status) && Number.isFinite(draft.savedAt) &&
      typeof draft.text === "string" && draft.text.length <= 1000 &&
      Number.isFinite(age) && age >= 0 && age < LOGIN_DRAFT_MAX_AGE ? draft.text : "";
  } catch (_) {
    return "";
  }
};
